import {
  IServiceAuditorCitation,
  IServiceAuditorFinding,
  ServiceAuditorCitationSourceType,
} from '@commenda/api/agent-platform/service-auditor/types';

export interface IServiceAuditCitationSource {
  text: string;
  // Set only for a document citation whose source id is a file id, so the source opens the file.
  file: { id: number; name: string } | null;
}

export interface IServiceAuditCitationView {
  // Source type, id and quote: the backend keeps citations unique on these.
  key: string;
  quote: string;
  source: IServiceAuditCitationSource;
}

export class ServiceAuditCitationHelper {
  static readonly PROOF_LABEL = 'Proof';

  static readonly SOURCE_NOT_RECORDED = 'Source not recorded';

  // Keyed by string because the stored source type is unchecked; unknown types get no prefix.
  private static readonly SOURCE_PREFIXES: Readonly<Record<string, string>> = {
    [ServiceAuditorCitationSourceType.FORM_ANSWER]: 'Form answer: ',
    [ServiceAuditorCitationSourceType.COMMENDA_RECORD]: 'Commenda record: ',
    [ServiceAuditorCitationSourceType.THREAD]: 'Thread message from ',
    [ServiceAuditorCitationSourceType.WORKFLOW_STEP]: 'Workflow step: ',
  };

  static getCitationViews(
    finding: Readonly<IServiceAuditorFinding>
  ): IServiceAuditCitationView[] {
    return finding.citations.map((citation) => ({
      key: `${citation.sourceType ?? ''}:${citation.sourceId ?? ''}:${citation.quote}`,
      quote: citation.quote,
      source: ServiceAuditCitationHelper.getCitationSource(citation),
    }));
  }

  // The backend keeps one free-text label per citation, so it fills the copy's slot after the type prefix.
  // A document with a file id stays openable without a label, named after its id.
  private static getCitationSource(
    citation: Readonly<IServiceAuditorCitation>
  ): IServiceAuditCitationSource {
    const label = citation.sourceLabel?.trim();
    if (citation.sourceType === ServiceAuditorCitationSourceType.DOCUMENT) {
      const fileId = ServiceAuditCitationHelper.getFileId(citation.sourceId);
      const fileName = label || (fileId === null ? null : `File ${fileId}`);
      if (fileName) {
        return {
          text: citation.page ? `${fileName}, page ${citation.page}` : fileName,
          file: fileId === null ? null : { id: fileId, name: fileName },
        };
      }
    }
    if (!label) {
      return {
        text: ServiceAuditCitationHelper.SOURCE_NOT_RECORDED,
        file: null,
      };
    }
    const prefix = citation.sourceType
      ? (ServiceAuditCitationHelper.SOURCE_PREFIXES[citation.sourceType] ?? '')
      : '';
    return { text: `${prefix}${label}`, file: null };
  }

  private static getFileId(sourceId: string | null): number | null {
    if (!sourceId || !/^\d+$/.test(sourceId.trim())) return null;
    const fileId = Number(sourceId.trim());
    return fileId > 0 ? fileId : null;
  }
}
